import React, { useEffect, useState, useCallback } from "react";
import { Grid, Typography, CircularProgress, Box } from "@mui/material";
import dayjs from "dayjs";

import style from "./index.module.css";

import ListWithIntersection from "@/components/ListWithIntersection";
import GetImages from "@/components/GetImages";
import DischargedPatientDetail from "../DischargedPatientDetail";
import AdmitPatientShortDetail from "../AdmitPatientShortDetail";
import { universalSearchStyles } from "@/views/Common/UniversalSearch/style";

import useIsTablet from "@/utils/hooks/useIsTablet";
import {
  calculateDayFromTargetDate,
  getLastMessageTime,
  getLatestTimestampFunc,
  transformClasses,
} from "@/utils";
import useIsMobile from "@/utils/hooks/useIsMobile";

import {
  IAdmitPatientPopUpsEnums,
  IPDashboardFilterType,
  IPDashboardPatientAdmitStatus,
} from "@/models/doctor";
import {
  IHospitalPatient,
  IHospitalPatientResponse,
} from "@/models/ApiRequestResponse/doctor";
import { IModalKeyEnums } from "@/models/permission";
import { IIPDashboardPermissionEnums } from "@/models/permission";
import { ISessionKeys } from "@/models";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  fetchAdmitPatientDetails,
  fetchDoctorHospitalPatientListing,
  ipDashboardDetails,
  setOpenCloseAdmittedDischargeModal,
  setReAdmitPatient,
} from "@/store/slices/doctor/ipDashboard";
import { config, setIsShowUniversalSearch } from "@/store/slices/config";
import { setSelectedChat } from "@/store/slices/chat";
import { universalSearchFilterDetails } from "@/store/slices/doctor/universalSearchFilter";

import { useFirebaseMessagingContext } from "@/context/Chat";

import { storage } from "@/utils/Storage";
import { usePermissionRestriction } from "@/utils/hooks/usePermissionRestriction";

const IPDashboardCard: React.FC = () => {
  const [chatDetails, setChatDetails] = useState<{ [key: string]: any }>({});

  const {
    hospitalPatients,
    hospitalSearchPatients,
    admitPatientPopUps,
    patientDetail,
    isPatientListLoading,
    selectedHospitalId,
    chipFilter,
    selectRoomId,
  } = useAppSelector(ipDashboardDetails);

  const { isShowUniversalSearch } = useAppSelector(config);
  const { ipDashboard } = useAppSelector(universalSearchFilterDetails);

  const dispatch = useAppDispatch();
  const { listType, fetchConversationById, paginatedDocs } =
    useFirebaseMessagingContext();

  const currentUserId = storage.getSessionToken(ISessionKeys.USER_ID);
  const patientList: IHospitalPatientResponse = isShowUniversalSearch
    ? hospitalSearchPatients
    : hospitalPatients;

  const { isMobile } = useIsMobile();
  const { isTablet } = useIsTablet();
  const isMobileOrTablet = isMobile || isTablet;

  const permissionRestrictionHandler = usePermissionRestriction();

  const fetchMoreData = useCallback(() => {
    if (
      isPatientListLoading ||
      !hospitalPatients.hasMore ||
      isShowUniversalSearch
    ) {
      return;
    }

    const filterOptions =
      chipFilter === IPDashboardFilterType.DISCHARGE ||
      chipFilter === IPDashboardFilterType.ACTIVE
        ? {
            discharged: chipFilter === IPDashboardFilterType.DISCHARGE,
          }
        : {};

    dispatch(
      fetchDoctorHospitalPatientListing({
        hospitalId: selectedHospitalId,
        start_date: ipDashboard?.startDate ?? "",
        end_date: ipDashboard?.endDate ?? "",
        ...(selectRoomId !== "1" && { room_id: selectRoomId }),
        ...filterOptions,
        page: (hospitalPatients.page ?? 1) + 1,
      })
    );
  }, [
    isPatientListLoading,
    hospitalPatients.hasMore,
    hospitalPatients.page,
    isShowUniversalSearch,
    dispatch,
    selectedHospitalId,
    ipDashboard,
    selectRoomId,
    chipFilter,
  ]);

  const openCloseAdmitPatientHandler = (type: IAdmitPatientPopUpsEnums) => {
    dispatch(setOpenCloseAdmittedDischargeModal(type));
  };

  const patientDetailHandler = (patientDetail: IHospitalPatient) => {
    const isViewAdmitPatientPermissionGranted = permissionRestrictionHandler(
      IModalKeyEnums.IP_DASHBOARD,
      IIPDashboardPermissionEnums.VIEW_ADMITTED_PATIENT_DETAILS
    );
    if (!isViewAdmitPatientPermissionGranted) return;

    dispatch(fetchAdmitPatientDetails({ admitPatientId: patientDetail?.id }));
    dispatch(setReAdmitPatient(false));
    if (isMobileOrTablet) {
      openCloseAdmitPatientHandler(
        patientDetail?.status === IPDashboardPatientAdmitStatus.DISCHARGED
          ? IAdmitPatientPopUpsEnums.DISCHARGED
          : IAdmitPatientPopUpsEnums.ADMITTED
      );
    } else if (isShowUniversalSearch) {
      dispatch(setIsShowUniversalSearch(false));
    }
    fetchConversationById(patientDetail?.id).then((response) =>
      dispatch(setSelectedChat(response))
    );
  };

  const renderPatientCard = (patient: IHospitalPatient) => {
    const chatDetail = chatDetails[patient.id];
    const lastMessage = chatDetail?.lastMessage;
    const lastMsgsInternal = lastMessage?.internal;
    const lastMsgsInternlSeen = lastMsgsInternal?.seen;
    const lastMsgsPatient = lastMessage?.patient;
    const lastMsgsPatientSeen = lastMsgsPatient?.seen;

    const isStaffInvolved =
      chatDetail?.users?.staff_members?.includes(currentUserId) &&
      chatDetail?.users?.staff_members?.includes(lastMsgsPatient?.sender_id);

    const lastMsgInternal =
      lastMsgsInternal?.sender_id === currentUserId ? null : lastMsgsInternal;

    const lastMsgPatient = isStaffInvolved ? null : lastMsgsPatient;

    const hasMentions =
      currentUserId !== lastMsgInternal?.sender_id &&
      lastMsgsInternlSeen &&
      lastMsgsInternlSeen[currentUserId]?.mentions?.length > 0;

    let unreadMessageCount = 0;

    if (
      lastMsgsInternlSeen &&
      lastMsgsInternlSeen[currentUserId]?.unReadMessageCount > 0 &&
      lastMsgInternal?.sender_id !== currentUserId
    ) {
      unreadMessageCount =
        (lastMsgsInternlSeen &&
          lastMsgsInternlSeen[currentUserId]?.unReadMessageCount) ||
        0;
    } else if (
      lastMsgsPatientSeen &&
      lastMsgsPatientSeen[currentUserId]?.unReadMessageCount > 0 &&
      !isStaffInvolved
    ) {
      unreadMessageCount =
        (lastMsgsPatientSeen &&
          lastMsgsPatientSeen[currentUserId]?.unReadMessageCount) ||
        0;
    }

    const isUnread =
      unreadMessageCount > 0 &&
      (lastMsgInternal?.sender_id !== currentUserId ||
        lastMsgPatient?.sender_id !== currentUserId);

    const latestTimestamp = getLatestTimestampFunc(
      chatDetail?.lastMessage?.internal,
      lastMsgsPatient
    );

    const lastMessageTime = latestTimestamp
      ? getLastMessageTime(latestTimestamp)
      : null;

    return (
      <Grid
        item
        className={transformClasses(
          style.container,
          !isMobileOrTablet && patient?.id === patientDetail?.id
            ? style.active
            : ""
        )}
        sx={{
          backgroundColor:
            patient.status === IPDashboardPatientAdmitStatus.DISCHARGED
              ? "var(--warm-neutral-50)"
              : "",
        }}
        onClick={() => patientDetailHandler(patient)}
      >
        <Grid item className={style.patientDetail}>
          <Typography component={"p"} className={style.patientName}>
            {patient?.name}
          </Typography>
          <Typography component={"p"} className={style.admittedDateDuration}>
            {patient.status === IPDashboardPatientAdmitStatus.ADMIT ? (
              <>
                <GetImages name="GreenDotIcon" width="8" height="6" />
                {dayjs(patient?.admission_date).format("DD MMM")}
                &nbsp;
                {`(${calculateDayFromTargetDate(patient?.admission_date)})`}
              </>
            ) : (
              dayjs(patient?.discharged_date).format("DD MMM")
            )}
          </Typography>
        </Grid>
        <Grid item className={style.roomCaseHistory}>
          <Typography
            component={"p"}
            className={transformClasses(
              style.roomNo,
              patient.status === IPDashboardPatientAdmitStatus.DISCHARGED
                ? style.dischargedPatient
                : ""
            )}
            sx={{
              backgroundColor:
                patient.status === IPDashboardPatientAdmitStatus.DISCHARGED
                  ? "var(--peach-100)"
                  : "",
            }}
          >
            {patient.status === IPDashboardPatientAdmitStatus.DISCHARGED
              ? "Discharged"
              : `${patient?.room_name} - Bed ${patient?.number}`}
          </Typography>

          {chatDetail ? (
            <Grid item display={"flex"} alignItems={"center"}>
              {isUnread && (
                <Typography component={"p"} className={style.messageTime}>
                  {lastMessageTime}
                </Typography>
              )}
              {hasMentions ? (
                <Grid item className={style.messageStartMentionUnReadCount}>
                  <Grid item>
                    <GetImages name="StarMentionIcon" width="11" height="10" />
                  </Grid>
                  <Typography component={"p"}> {unreadMessageCount}</Typography>
                </Grid>
              ) : isUnread ? (
                <Grid item className={style.messageUnReadCount}>
                  <Typography component={"p"}> {unreadMessageCount}</Typography>
                </Grid>
              ) : (
                <Typography component={"p"} className={style.caseHistory}>
                  {patient?.case_history}
                </Typography>
              )}
            </Grid>
          ) : (
            <Typography component={"p"} className={style.caseHistory}>
              {patient?.case_history}
            </Typography>
          )}
        </Grid>
      </Grid>
    );
  };

  const renderModals = () => (
    <>
      {admitPatientPopUps.isDischarged && (
        <DischargedPatientDetail
          isOpen={admitPatientPopUps.isDischarged}
          closeHandler={() =>
            openCloseAdmitPatientHandler(IAdmitPatientPopUpsEnums.DISCHARGED)
          }
        />
      )}
      {admitPatientPopUps.isAdmitted && (
        <AdmitPatientShortDetail
          isOpen={admitPatientPopUps.isAdmitted}
          closeHandler={() =>
            openCloseAdmitPatientHandler(IAdmitPatientPopUpsEnums.ADMITTED)
          }
        />
      )}
    </>
  );

  useEffect(() => {
    if (!paginatedDocs || paginatedDocs.length === 0) {
      return;
    }
    const detailsMap = paginatedDocs.reduce((acc, doc) => {
      if (doc.id) {
        acc[doc.id] = doc;
      }
      return acc;
    }, {} as { [key: string]: any });

    setChatDetails(detailsMap);
  }, [paginatedDocs]);

  useEffect(() => {
    const updatedChatDetails: { [key: string]: any } = {};
    patientList?.data?.forEach((patient: IHospitalPatient) => {
      fetchConversationById(patient.id).then((response) => {
        if (response) {
          updatedChatDetails[patient.id] = response;
        }
      });
    });
    setChatDetails(updatedChatDetails);
  }, [hospitalPatients, hospitalSearchPatients, currentUserId, listType]);
  return (
    <>
      <ListWithIntersection
        list={patientList?.data ?? []}
        onLoadMore={fetchMoreData}
        muiClasses={{
          ...(isShowUniversalSearch
            ? {
                ...universalSearchStyles.cardContainer,
                ...universalSearchStyles.pharmacyOrDiagnosesCardContainer,
              }
            : {}),
        }}
      >
        {patientList?.data?.map((patient: IHospitalPatient) => (
          <ListWithIntersection.Item
            key={patient.id}
            dataId={String(patient.id)}
          >
            {renderPatientCard(patient)}
          </ListWithIntersection.Item>
        ))}
      </ListWithIntersection>

      {isPatientListLoading && (hospitalPatients.page ?? 1) > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {isMobileOrTablet
        ? renderModals()
        : isShowUniversalSearch && renderModals()}
    </>
  );
};

export default IPDashboardCard;
